'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import CarpetMultiSizeEditor from './CarpetMultiSizeEditor';
import ServicePricingNotes from './ServicePricingNotes';
import {
  CarpetItem,
  carpetLineCost,
  normalizeCarpetItems,
} from '@/lib/carpetMeasurements';
import {
  CurtainItem,
  curtainLineCost,
  normalizeCurtainItems,
} from '@/lib/curtainMeasurements';

interface SeatMeasurement {
  seatNumber: number;
  width: number; // in feet
}

interface ServiceMeasurementData {
  // Sofa measurements
  numberOfSeats?: number;
  seatMeasurements?: SeatMeasurement[];

  // Curtain measurements — multi-size: each curtainItem is N panels of
  // one width × length. Legacy single-size fields remain readable.
  curtainItems?: CurtainItem[];
  curtainWidth?: number;
  curtainLength?: number;
  numberOfPanels?: number;

  // Carpet measurements — multi-size: each carpetItem has its own
  // width/length/quantity. The legacy single-size fields are kept readable
  // for back-compat with bookings persisted before the feature shipped.
  carpetItems?: CarpetItem[];
  carpetWidth?: number;
  carpetLength?: number;
  numberOfCarpets?: number;

  // Upholstery
  quantity?: number;

  // Common
  measurementUnit?: 'feet' | 'meters';
  images?: string[]; // Base64 or URLs
}

interface ServiceMeasurementStepProps {
  serviceType: 'sofa' | 'curtain' | 'carpet' | 'upholstery';
  serviceName: string;
  basePrice: number;
  unit: string;
  measurements: ServiceMeasurementData;
  onMeasurementChange: (measurements: ServiceMeasurementData) => void;
}

const ServiceMeasurementStep = ({
  serviceType,
  serviceName,
  basePrice,
  unit,
  measurements,
  onMeasurementChange,
}: ServiceMeasurementStepProps) => {
  const [unitSystem, setUnitSystem] = useState<'feet' | 'meters'>(
    measurements.measurementUnit || 'feet'
  );

  const updateMeasurements = (updates: Partial<ServiceMeasurementData>) => {
    onMeasurementChange({
      ...measurements,
      ...updates,
      measurementUnit: unitSystem,
    });
  };

  // Auto-seed sofa defaults on first mount so the estimate shows immediately
  // without requiring the user to touch the input. We only seed when no value
  // is set, so this never overwrites the user's edits.
  useEffect(() => {
    if (
      serviceType === 'sofa' &&
      (measurements.numberOfSeats === undefined ||
        !measurements.seatMeasurements ||
        measurements.seatMeasurements.length === 0)
    ) {
      onMeasurementChange({
        ...measurements,
        numberOfSeats: measurements.numberOfSeats ?? 1,
        seatMeasurements:
          measurements.seatMeasurements && measurements.seatMeasurements.length > 0
            ? measurements.seatMeasurements
            : [{ seatNumber: 1, width: 2 }],
        measurementUnit: measurements.measurementUnit || unitSystem,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  const handleUnitChange = (newUnit: 'feet' | 'meters') => {
    setUnitSystem(newUnit);
    updateMeasurements({ measurementUnit: newUnit });
  };

  // Rates (basePrice) are stored per sq.ft. If the user enters dimensions in
  // meters, convert sq.m → sq.ft before multiplying by the rate.
  const SQM_TO_SQFT = 10.764;
  const toSqFt = (area: number): number => (unitSystem === 'meters' ? area * SQM_TO_SQFT : area);

  const calculateCost = (): number => {
    switch (serviceType) {
      case 'sofa':
        if (measurements.numberOfSeats && measurements.numberOfSeats > 0) {
          return measurements.numberOfSeats * basePrice;
        }
        return 0;

      case 'curtain':
        // Multi-size curtain: helper sums every row and applies the unit
        // conversion internally. Returns null when no usable rows exist.
        return curtainLineCost(measurements, basePrice) ?? 0;

      case 'carpet':
        // Multi-size carpet: helper sums every row and applies the unit
        // conversion internally. Returns null when there are no usable rows.
        return carpetLineCost(measurements, basePrice) ?? 0;

      case 'upholstery':
        if (measurements.quantity && measurements.quantity > 0) {
          return measurements.quantity * basePrice;
        }
        return 0;

      default:
        return 0;
    }
  };

  const renderSofaMeasurements = () => {
    const numberOfSeats = measurements.numberOfSeats || 1;
    const seatMeasurements = measurements.seatMeasurements || [];

    return (
      <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:148:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="148" data-component-end-line="223" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
        <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:149:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="149" data-component-end-line="164" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20bg-%5B%23E4EDF0%5D%20border%20border-%5B%23204653%5D%2F20%20rounded-xl%20flex%20items-start%20gap-3%22%7D" className="p-4 bg-[#E4EDF0] border border-[#204653]/20 rounded-xl flex items-start gap-3">
          <Icon data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:150:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="150" data-component-end-line="154" data-component-file="ServiceMeasurementStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22text-%5B%23204653%5D%20flex-shrink-0%20mt-0.5%22%7D"
            name="InformationCircleIcon"
            size={20}
            className="text-[#204653] flex-shrink-0 mt-0.5"
          />
          <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:155:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="155" data-component-end-line="163" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:156:12" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="156" data-component-end-line="158" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%20text-%5B%23204653%5D%22%2C%22textContent%22%3A%22Measurements%20are%20optional%20for%20sofa%20nanocoating%22%7D" className="text-sm font-medium text-[#204653]">
              Measurements are optional for sofa nanocoating
            </p>
            <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:159:12" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="159" data-component-end-line="162" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%23204653%5D%2F80%20mt-1%22%2C%22textContent%22%3A%22You%20can%20fill%20them%20in%20for%20an%20upfront%20estimate%2C%20or%20skip%20%E2%80%94%20our%20technician%20will%20measure%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20on-site%20and%20confirm%20the%20final%20price.%22%7D" className="text-xs text-[#204653]/80 mt-1">
              You can fill them in for an upfront estimate, or skip — our technician will measure
              on-site and confirm the final price.
            </p>
          </div>
        </div>

        <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:166:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="166" data-component-end-line="189" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:167:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="167" data-component-end-line="169" data-component-file="ServiceMeasurementStep.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22Number%20of%20Seats%22%7D" className="block text-sm font-semibold text-[#1D1D1F] mb-2">
            Number of Seats <span data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:168:28" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="168" data-component-end-line="168" data-component-file="ServiceMeasurementStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-normal%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22(Optional)%22%7D" className="text-xs font-normal text-[#86868B]">(Optional)</span>
          </label>
          <input data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:170:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="170" data-component-end-line="187" data-component-file="ServiceMeasurementStep.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3AnumberOfSeats%5D%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20border-2%20border-gray-200%20rounded-xl%20focus%3Aborder-%5B%23204653%5D%20focus%3Aoutline-none%20transition-colors%22%7D"
            type="number"
            min="1"
            max="20"
            value={numberOfSeats}
            onChange={(e) => {
              const seats = parseInt(e.target.value) || 1;
              const newSeatMeasurements = Array.from(
                { length: seats },
                (_, i) => seatMeasurements[i] || { seatNumber: i + 1, width: 2 }
              );
              updateMeasurements({
                numberOfSeats: seats,
                seatMeasurements: newSeatMeasurements,
              });
            }}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#204653] focus:outline-none transition-colors"
          />
          <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:188:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="188" data-component-end-line="188" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20mt-1%22%2C%22textContent%22%3A%221%20seat%20%3D%20maximum%202%20feet%20width%22%7D" className="text-xs text-[#86868B] mt-1">1 seat = maximum 2 feet width</p>
        </div>

        {numberOfSeats > 0 && (
          <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:192:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="192" data-component-end-line="221" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            <h3 data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:193:12" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="193" data-component-end-line="193" data-component-file="ServiceMeasurementStep.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22Measurements%20for%20Each%20Seat%22%7D" className="text-lg font-semibold text-[#1D1D1F]">Measurements for Each Seat</h3>
            {Array.from({ length: numberOfSeats }).map((_, index) => {
              const seatMeasurement = seatMeasurements[index] || {
                seatNumber: index + 1,
                width: 2,
              };
              return (
                <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:200:16" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="200" data-component-end-line="218" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20bg-gray-50%20rounded-xl%20border%20border-gray-200%22%7D" key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <label data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:201:18" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="201" data-component-end-line="203" data-component-file="ServiceMeasurementStep.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22Seat%20-%20Width%20(%20)%22%7D" className="block text-sm font-medium text-[#1D1D1F] mb-2">
                    Seat {index + 1} - Width ({unitSystem === 'feet' ? 'feet' : 'meters'})
                  </label>
                  <input data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:204:18" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="204" data-component-end-line="217" data-component-file="ServiceMeasurementStep.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22className%22%3A%22w-full%20px-4%20py-2%20border-2%20border-gray-200%20rounded-lg%20focus%3Aborder-%5B%23204653%5D%20focus%3Aoutline-none%20transition-colors%22%7D"
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.1"
                    value={seatMeasurement.width}
                    onChange={(e) => {
                      const width = parseFloat(e.target.value) || 2;
                      const updated = [...seatMeasurements];
                      updated[index] = { seatNumber: index + 1, width };
                      updateMeasurements({ seatMeasurements: updated });
                    }}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#204653] focus:outline-none transition-colors"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCurtainMeasurements = () => {
    // Pass RAW curtainItems so partial rows (width typed but length still
    // empty) survive re-renders. Normalizer is used only to hydrate from
    // legacy single-size fields when curtainItems is absent.
    const items: CurtainItem[] =
      measurements.curtainItems && measurements.curtainItems.length > 0
        ? measurements.curtainItems
        : normalizeCurtainItems(measurements);
    return (
      <CarpetMultiSizeEditor data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:236:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="236" data-component-end-line="251" data-component-file="ServiceMeasurementStep.tsx" data-component-name="CarpetMultiSizeEditor" data-component-content="%7B%22elementName%22%3A%22CarpetMultiSizeEditor%22%7D"
        items={items}
        unit={unitSystem}
        pricePerSqft={basePrice}
        kind="curtain"
        onChange={(next) => {
          // Persist only the new array shape; clear the legacy single-
          // size fields so old + new don't drift apart on re-edit.
          updateMeasurements({
            curtainItems: next,
            curtainWidth: undefined,
            curtainLength: undefined,
            numberOfPanels: undefined,
          });
        }}
      />
    );
  };

  const renderCarpetMeasurements = () => {
    // Pass the RAW carpetItems through so partially-entered rows survive
    // re-renders. (Using normalizeCarpetItems here would filter out any
    // row where width OR length OR quantity is still 0, which wipes the
    // user's first keystroke on every empty row — see chat-history bug.)
    //
    // The normalizer's only job here is to hydrate the editor on a
    // legacy single-size booking that doesn't yet have carpetItems[].
    const items: CarpetItem[] =
      measurements.carpetItems && measurements.carpetItems.length > 0
        ? measurements.carpetItems
        : normalizeCarpetItems(measurements);
    return (
      <CarpetMultiSizeEditor data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:268:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="268" data-component-end-line="283" data-component-file="ServiceMeasurementStep.tsx" data-component-name="CarpetMultiSizeEditor" data-component-content="%7B%22elementName%22%3A%22CarpetMultiSizeEditor%22%7D"
        items={items}
        unit={unitSystem}
        pricePerSqft={basePrice}
        onChange={(next) => {
          // Persist only the new array shape — clear the legacy single-
          // size fields so they don't double-render or drift out of sync
          // when an old booking is being re-edited.
          updateMeasurements({
            carpetItems: next,
            carpetWidth: undefined,
            carpetLength: undefined,
            numberOfCarpets: undefined,
          });
        }}
      />
    );
  };

  const renderUpholsteryMeasurements = () => {
    return (
      <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:289:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="289" data-component-end-line="301" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
        <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:290:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="290" data-component-end-line="300" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:291:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="291" data-component-end-line="291" data-component-file="ServiceMeasurementStep.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22Quantity%20*%22%7D" className="block text-sm font-semibold text-[#1D1D1F] mb-2">Quantity *</label>
          <input data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:292:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="292" data-component-end-line="299" data-component-file="ServiceMeasurementStep.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20border-2%20border-gray-200%20rounded-xl%20focus%3Aborder-%5B%23204653%5D%20focus%3Aoutline-none%20transition-colors%22%7D"
            type="number"
            min="1"
            max="50"
            value={measurements.quantity || ''}
            onChange={(e) => updateMeasurements({ quantity: parseInt(e.target.value) || 1 })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#204653] focus:outline-none transition-colors"
          />
        </div>
      </div>
    );
  };

  const isMeasurementValid = (): boolean => {
    switch (serviceType) {
      case 'sofa':
        return !!(
          measurements.numberOfSeats &&
          measurements.numberOfSeats > 0 &&
          measurements.seatMeasurements &&
          measurements.seatMeasurements.length === measurements.numberOfSeats
        );
      case 'curtain':
        // Valid as long as at least one row has positive width/length/
        // panels. Normalizer also accepts a legacy single-size shape.
        return normalizeCurtainItems(measurements).length > 0;
      case 'carpet':
        // Valid as long as at least one row in carpetItems[] has positive
        // width/length/quantity. The normalizer also accepts a legacy
        // single-size shape for back-compat.
        return normalizeCarpetItems(measurements).length > 0;
      case 'upholstery':
        return !!(measurements.quantity && measurements.quantity > 0);
      default:
        return false;
    }
  };

  const calculatedCost = calculateCost();

  return (
    <motion.div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:333:4" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="333" data-component-end-line="443" data-component-file="ServiceMeasurementStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22space-y-8%22%7D"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:339:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="339" data-component-end-line="348" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <h2 data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:340:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="340" data-component-end-line="342" data-component-file="ServiceMeasurementStep.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-semibold%20text-%5B%231D1D1F%5D%20mb-3%20font-heading%20tracking-tight%22%7D" className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-3 font-heading tracking-tight">
          {serviceType === 'sofa' ? 'Provide Measurements (Optional)' : 'Provide Measurements'}
        </h2>
        <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:343:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="343" data-component-end-line="347" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%2386868B%5D%20text-lg%22%7D" className="text-[#86868B] text-lg">
          {serviceType === 'sofa'
            ? `Optional — share rough measurements for an upfront estimate, or skip and our technician will measure your ${serviceName} on-site.`
            : `Help us calculate the exact cost for your ${serviceName}`}
        </p>
      </div>

      {/* Unit Selection */}
      <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:351:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="351" data-component-end-line="375" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%20p-4%20bg-gray-50%20rounded-xl%22%7D" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
        <span data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:352:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="352" data-component-end-line="352" data-component-file="ServiceMeasurementStep.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22Measurement%20Unit%3A%22%7D" className="text-sm font-medium text-[#1D1D1F]">Measurement Unit:</span>
        <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:353:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="353" data-component-end-line="374" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%22%7D" className="flex gap-2">
          <button data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:354:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="354" data-component-end-line="363" data-component-file="ServiceMeasurementStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Feet%22%7D"
            onClick={() => handleUnitChange('feet')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              unitSystem === 'feet'
                ? 'bg-[#204653] text-white'
                : 'bg-white text-[#1D1D1F] border-2 border-gray-200'
            }`}
          >
            Feet
          </button>
          <button data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:364:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="364" data-component-end-line="373" data-component-file="ServiceMeasurementStep.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Meters%22%7D"
            onClick={() => handleUnitChange('meters')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              unitSystem === 'meters'
                ? 'bg-[#204653] text-white'
                : 'bg-white text-[#1D1D1F] border-2 border-gray-200'
            }`}
          >
            Meters
          </button>
        </div>
      </div>

      {/* Service-specific measurements */}
      <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:378:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="378" data-component-end-line="383" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20bg-white%20border-2%20border-gray-200%20rounded-2xl%22%7D" className="p-6 bg-white border-2 border-gray-200 rounded-2xl">
        {serviceType === 'sofa' && renderSofaMeasurements()}
        {serviceType === 'curtain' && renderCurtainMeasurements()}
        {serviceType === 'carpet' && renderCarpetMeasurements()}
        {serviceType === 'upholstery' && renderUpholsteryMeasurements()}
      </div>

      {/* Cost Calculation */}
      {isMeasurementValid() && (
        <motion.div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:387:8" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="387" data-component-end-line="435" data-component-file="ServiceMeasurementStep.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22p-6%20bg-gradient-to-br%20from-%5B%23204653%5D%20to-%5B%231a3a44%5D%20rounded-2xl%20text-white%22%7D"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-6 bg-gradient-to-br from-[#204653] to-[#1a3a44] rounded-2xl text-white"
        >
          <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:392:10" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="392" data-component-end-line="434" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
            <div data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:393:12" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="393" data-component-end-line="432" data-component-file="ServiceMeasurementStep.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:394:14" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="394" data-component-end-line="394" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20opacity-90%20mb-1%22%2C%22textContent%22%3A%22Estimated%20Cost%22%7D" className="text-sm opacity-90 mb-1">Estimated Cost</p>
              <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:395:14" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="395" data-component-end-line="395" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20font-bold%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-3xl font-bold">₹{calculatedCost.toLocaleString('en-IN')}</p>
              <p data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:396:14" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="396" data-component-end-line="431" data-component-file="ServiceMeasurementStep.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20opacity-75%20mt-1%22%7D" className="text-xs opacity-75 mt-1">
                {serviceType === 'sofa' &&
                  `${measurements.numberOfSeats} seat${measurements.numberOfSeats !== 1 ? 's' : ''} × ₹${basePrice.toLocaleString('en-IN')}`}
                {serviceType === 'curtain' &&
                  (() => {
                    // Multi-size: same rounding as curtainLineCost so the
                    // displayed "X sq.ft × ₹Y/sq.ft" reconciles cleanly.
                    const items = normalizeCurtainItems(measurements);
                    const count = items.reduce((s, c) => s + c.quantity, 0);
                    const sqft = Math.round(
                      items.reduce(
                        (s, c) => s + toSqFt(c.width * c.length * c.quantity),
                        0
                      )
                    );
                    return `${count} panel${count !== 1 ? 's' : ''} · ${sqft} sq.ft × ₹${basePrice.toLocaleString('en-IN')}/sq.ft`;
                  })()}
                {serviceType === 'carpet' &&
                  (() => {
                    // Multi-size: sum every row's area in sq.ft so the
                    // displayed "X sq.ft × ₹Y/sq.ft" still reconciles to
                    // the calculatedCost above (rounding identical to
                    // carpetLineCost).
                    const items = normalizeCarpetItems(measurements);
                    const count = items.reduce((s, c) => s + c.quantity, 0);
                    const sqft = Math.round(
                      items.reduce(
                        (s, c) => s + toSqFt(c.width * c.length * c.quantity),
                        0
                      )
                    );
                    return `${count} carpet${count !== 1 ? 's' : ''} · ${sqft} sq.ft × ₹${basePrice.toLocaleString('en-IN')}/sq.ft`;
                  })()}
                {serviceType === 'upholstery' &&
                  `${measurements.quantity} item${measurements.quantity !== 1 ? 's' : ''} × ₹${basePrice.toLocaleString('en-IN')}`}
              </p>
            </div>
            <Icon data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:433:12" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="433" data-component-end-line="433" data-component-file="ServiceMeasurementStep.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CalculatorIcon%22%2C%22className%22%3A%22opacity-50%22%7D" name="CalculatorIcon" size={48} className="opacity-50" />
          </div>
        </motion.div>
      )}

      {/* Pricing disclaimers — moved here from the service-selection card.
          Customers see these AFTER entering their measurements, when the
          caveats ("final area confirmed on-site", "wool charged
          differently") are most relevant. */}
      <ServicePricingNotes data-component-id="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx:442:6" data-component-path="src/app/service-booking-flow/components/ServiceMeasurementStep.tsx" data-component-line="442" data-component-end-line="442" data-component-file="ServiceMeasurementStep.tsx" data-component-name="ServicePricingNotes" data-component-content="%7B%22elementName%22%3A%22ServicePricingNotes%22%7D" kind={serviceType} />
    </motion.div>
  );
};

export default ServiceMeasurementStep;
